import {FunctionComponent, useEffect, useMemo} from "react";
import {MapContainer, TileLayer, Marker, Popup, useMap} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {Consultant} from "../../../../types";
import ConsultantPopupCard from "../ConsultantPopupCard";
import {DEFAULT_CENTER, DEFAULT_ZOOM} from "../../regionCenters";

// Fix Leaflet default marker icon issue with webpack/CRA
import iconUrl from "leaflet/dist/images/marker-icon.png";
import iconRetinaUrl from "leaflet/dist/images/marker-icon-2x.png";
import shadowUrl from "leaflet/dist/images/marker-shadow.png";

// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl,
  iconRetinaUrl,
  shadowUrl,
});

interface MapProps {
  consultants: Consultant[];
  center?: { lat: number; lng: number };
  zoom?: number;
}

interface MapControllerProps {
  center: { lat: number; lng: number };
  zoom: number;
}

const MapController: FunctionComponent<MapControllerProps> = ({center, zoom}) => {
  const map = useMap();

  useEffect(() => {
    map.flyTo([center.lat, center.lng], zoom, {duration: 1.5});
  }, [center, zoom, map]);

  return null;
};

const ConsultantMap: FunctionComponent<MapProps> = ({
  consultants,
  center = DEFAULT_CENTER,
  zoom = DEFAULT_ZOOM,
}) => {
  // Group consultants by location
  const grouped = useMemo(() => {
    const groups: Record<string, Consultant[]> = {};
    consultants.forEach((c) => {
      const key = `${c.latitude},${c.longitude}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(c);
    });
    return groups;
  }, [consultants]);

  return (
    <div className="rounded-lg overflow-hidden border border-gray-300 shadow-sm">
      <MapContainer
        center={[center.lat, center.lng]}
        zoom={zoom}
        style={{height: "500px", width: "100%"}}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapController center={center} zoom={zoom} />

        {Object.entries(grouped).map(([key, group]) => {
          const [lat, lng] = key.split(",").map(Number);
          return (
            <Marker key={key} position={[lat, lng]}>
              <Popup minWidth={280} maxHeight={250}>
                <div className="consultant-popup">
                  {group.length > 1 && (
                    <p className="text-xs text-gray-500 mb-2 font-medium">
                      {group.length} poradcov na tomto mieste
                    </p>
                  )}
                  <div style={{maxHeight: "200px", overflowY: group.length > 2 ? "auto" : "visible"}}>
                    {group.map((consultant) => (
                      <ConsultantPopupCard key={consultant.id} consultant={consultant} />
                    ))}
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
};

export default ConsultantMap;
